import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/library/dialog";
import { Input } from "@/components/ui/input";
import NormalSelector from "@/components/ui/others/NormalSelector";
import { SingleDatePicker } from "@/components/ui/library/single-date-picker";
import { SegmentToggle } from "@/components/ui/SegmentedToggle";
import { Button } from "@/components/ui/button";
import { X, CalendarClock, AlertTriangle, Check, Sparkles, Plus, Trash2 } from "lucide-react";
import {
  buildSchedulePreview,
  todayKey,
  type InstallmentFrequency,
} from "./installmentMath";
import type { InstallmentPlan, InstallmentTemplate } from "./types";
import {
  useCreateInstallmentPlan,
  useRebuildInstallmentPlan,
} from "@/features/leads/api/useInstallmentMutations";

/**
 * Set up (or reshape) a deal's EMI plan.
 *
 * The right-hand pane is the point of this screen. The operator is committing a
 * customer to 24 dated obligations, so they see every one of them — number, due
 * date, amount — plus a footer that proves the rows add up to the principal,
 * BEFORE anything is written. A finance UI that asks you to trust an unseen
 * calculation has failed at the only job it has.
 *
 * A Radix Dialog rather than a Sheet: `ViewLead` is itself a Sheet, and
 * `AttachCourseModal` already proves a wide Dialog nests inside it cleanly,
 * whereas Sheet-in-Sheet is unproven in this codebase.
 */

const FREQUENCY_OPTIONS: { value: InstallmentFrequency; name: string }[] = [
  { value: "MONTHLY", name: "Monthly" },
  { value: "QUARTERLY", name: "Quarterly" },
  { value: "FORTNIGHTLY", name: "Fortnightly" },
  { value: "WEEKLY", name: "Weekly" },
  { value: "HALF_YEARLY", name: "Half-yearly" },
  { value: "YEARLY", name: "Yearly" },
];

function money(n: number): string {
  const hasPaise = Math.round(n * 100) % 100 !== 0;
  return `₹${n.toLocaleString("en-IN", {
    minimumFractionDigits: hasPaise ? 2 : 0,
    maximumFractionDigits: 2,
  })}`;
}

function longDate(key: string): string {
  const [y, m, d] = key.split("-");
  if (!y || !m || !d) return key;
  return new Date(Date.UTC(Number(y), Number(m) - 1, Number(d))).toLocaleDateString(
    "en-IN",
    { day: "2-digit", month: "short", year: "numeric", timeZone: "UTC" },
  );
}

/** First of next month — a sane default that isn't "today". */
function defaultStart(): string {
  const now = new Date();
  const y = now.getMonth() === 11 ? now.getFullYear() + 1 : now.getFullYear();
  const m = (now.getMonth() + 1) % 12;
  return `${y}-${String(m + 1).padStart(2, "0")}-01`;
}

interface Props {
  open: boolean;
  onClose: () => void;
  childId: number;
  dealName: string;
  /** What the deal still owes — the default principal. */
  pendingAmount: number;
  /** Present when editing/rebuilding rather than creating. */
  plan?: InstallmentPlan | null;
  /** Prefill from the attached programme, when it carries one. */
  template?: InstallmentTemplate | null;
}

export default function InstallmentPlanModal({
  open,
  onClose,
  childId,
  dealName,
  pendingAmount,
  plan = null,
  template = null,
}: Props) {
  const isEdit = !!plan;

  const [principal, setPrincipal] = useState(String(pendingAmount || ""));
  const [mode, setMode] = useState<"count" | "amount" | "custom">("count");
  /** Custom ladder rows as typed (amount text, YYYY-MM-DD). */
  const [customRows, setCustomRows] = useState<Array<{ amount: string; dueDate: string }>>([]);
  const [count, setCount] = useState("12");
  const [perAmount, setPerAmount] = useState("");
  const [frequency, setFrequency] = useState<InstallmentFrequency>("MONTHLY");
  const [startDate, setStartDate] = useState(defaultStart());
  const [remindDaysBefore, setRemindDaysBefore] = useState("3");
  const [touched, setTouched] = useState(false);

  /**
   * Rows money has already touched. They are frozen: never re-dated, re-priced
   * or renumbered, because receipts exist against them. Everything this dialog
   * does applies to the rest.
   */
  const paidInstallments = useMemo(
    () =>
      (plan?.installments ?? []).filter(
        (i) =>
          i.status === "PAID" ||
          i.status === "WAIVED" ||
          i.paidAmount > 0 ||
          i.waivedAmount > 0,
      ),
    [plan],
  );
  const openInstallments = useMemo(
    () => (plan?.installments ?? []).filter((i) => !paidInstallments.includes(i)),
    [plan, paidInstallments],
  );
  const frozenTotal = paidInstallments.reduce((s, i) => s + i.amount, 0);

  const createPlan = useCreateInstallmentPlan();
  const rebuildPlan = useRebuildInstallmentPlan();
  const saving = createPlan.isPending || rebuildPlan.isPending;

  /**
   * Seed from the template (or the existing plan) when the modal opens.
   * `touched` guards it so a re-render can never stomp on what the user is
   * halfway through typing.
   */
  useEffect(() => {
    if (!open || touched) return;
    if (plan) {
      // Editing changes the UNPAID tail only, so the field is about what is
      // still to collect — not the plan's original total. Showing the original
      // was the confusion: the number on screen had nothing to do with what the
      // Save button would actually change.
      setPrincipal(String(pendingAmount || ""));
      setCount(String(openInstallments.length || plan.installmentCount));
      setFrequency(plan.frequency);
      setStartDate(
        (openInstallments[0]?.dueDate ?? plan.startDate).slice(0, 10),
      );
      setRemindDaysBefore(String(plan.reminderDaysBefore));
      return;
    }
    setPrincipal(String(pendingAmount || ""));
    if (template) {
      setCount(String(template.installmentCount));
      setFrequency(template.frequency);
      setRemindDaysBefore(String(template.reminderDaysBefore));
    }
  }, [open, plan, template, pendingAmount, touched]);

  // Reset the "user has typed" latch each time the modal is reopened.
  useEffect(() => {
    if (!open) setTouched(false);
  }, [open]);

  const principalNum = Number(principal) || 0;

  const equalPreview = useMemo(
    () =>
      buildSchedulePreview({
        principal: principalNum,
        frequency,
        startDate,
        count: mode === "count" ? Number(count) : null,
        amountPerInstallment: mode === "amount" ? Number(perAmount) : null,
      }),
    [principalNum, frequency, startDate, mode, count, perAmount],
  );

  /**
   * Custom mode: the operator's own rows, numbered by due date the way the
   * server stores them, with the same "Σ = amount to finance" proof.
   */
  const preview = useMemo(() => {
    if (mode !== "custom") return equalPreview;
    const errors: string[] = [];
    const parsed = customRows.map((r, i) => {
      const amount = Number(r.amount);
      if (!(amount > 0)) errors.push(`Instalment ${i + 1} needs an amount above zero.`);
      if (!/^\d{4}-\d{2}-\d{2}$/.test(r.dueDate)) errors.push(`Instalment ${i + 1} needs a due date.`);
      return { amount: Math.round((amount || 0) * 100) / 100, dueDate: r.dueDate, given: i };
    });
    if (!customRows.length) errors.push("Add at least one instalment.");
    const rows = [...parsed]
      .sort((a, b) => (a.dueDate < b.dueDate ? -1 : a.dueDate > b.dueDate ? 1 : a.given - b.given))
      .map((r, i) => ({ seq: i + 1, amount: r.amount, dueDate: r.dueDate }));
    const totalPaise = parsed.reduce((s, r) => s + Math.round(r.amount * 100), 0);
    const warnings = parsed.some((r, i) => i > 0 && r.dueDate < parsed[i - 1]!.dueDate)
      ? ["Instalments are numbered by due date, so they will be saved in date order."]
      : [];
    return {
      rows,
      total: totalPaise / 100,
      balances: totalPaise === Math.round(principalNum * 100),
      anchorDay: equalPreview.anchorDay,
      errors,
      warnings,
    };
  }, [mode, customRows, equalPreview, principalNum]);

  const setCustomRow = (i: number, patch: Partial<{ amount: string; dueDate: string }>) => {
    setTouched(true);
    setCustomRows((rows) => rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  };
  const addCustomRow = () => {
    setTouched(true);
    setCustomRows((rows) => {
      const last = rows[rows.length - 1]?.dueDate ?? startDate;
      const d = new Date(`${last}T00:00:00`);
      d.setMonth(d.getMonth() + 1);
      const next = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      const left = Math.round((principalNum - rows.reduce((s, r) => s + (Number(r.amount) || 0), 0)) * 100) / 100;
      return [...rows, { amount: left > 0 ? String(left) : "", dueDate: next }];
    });
  };

  /**
   * The derived half of the count/amount pair. Whichever the operator is not
   * editing is shown, read-only, computed from the preview — so the two can
   * never disagree with the table below them.
   */
  const derived = useMemo(() => {
    if (!equalPreview.rows.length) return { count: "", perAmount: "" };
    return {
      count: String(equalPreview.rows.length),
      perAmount: String(equalPreview.rows[0]?.amount ?? ""),
    };
  }, [equalPreview]);

  const gap = principalNum - pendingAmount;
  const canSave =
    preview.errors.length === 0 && preview.rows.length > 0 && preview.balances && !saving;

  const handleSave = async () => {
    if (!canSave) return;
    if (mode === "custom") {
      const rows = preview.rows.map((r) => ({ amount: r.amount, dueDate: r.dueDate }));
      try {
        if (isEdit && plan) {
          await rebuildPlan.mutateAsync({ planId: plan.id, remainingPrincipal: principalNum, rows });
        } else {
          await createPlan.mutateAsync({
            childId,
            principal: principalNum,
            frequency: "CUSTOM",
            startDate: rows[0]!.dueDate,
            count: rows.length,
            amountPerInstallment: null,
            rows,
            reminderDaysBefore: Number(remindDaysBefore) || 0,
          });
        }
        onClose();
      } catch {
        /* the hooks toast; keep the modal open so nothing is retyped */
      }
      return;
    }
    const body = {
      childId,
      principal: principalNum,
      frequency,
      startDate,
      count: mode === "count" ? Number(count) : null,
      amountPerInstallment: mode === "amount" ? Number(perAmount) : null,
      reminderDaysBefore: Number(remindDaysBefore) || 0,
    };
    try {
      if (isEdit && plan) {
        await rebuildPlan.mutateAsync({
          planId: plan.id,
          remainingPrincipal: principalNum,
          count: mode === "count" ? Number(count) : undefined,
          amountPerInstallment:
            mode === "amount" ? Number(perAmount) : undefined,
          frequency,
          startDate,
        });
      } else {
        await createPlan.mutateAsync(body);
      }
      onClose();
    } catch {
      /* the hooks toast; keep the modal open so nothing is retyped */
    }
  };

  const today = todayKey();

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-[min(96vw,980px)] sm:max-w-[980px] rounded-[24px] p-0 gap-0 max-h-[min(88vh,720px)] flex flex-col overflow-hidden [&>button]:hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#e6e8e7]">
          <div className="flex items-center gap-2.5">
            <div className="size-8 rounded-xl bg-[#eaf5f1] flex items-center justify-center">
              <CalendarClock className="size-4 text-[#218760]" />
            </div>
            <div>
              <p className="text-[14px] font-medium tracking-[-0.28px] text-[#1e302a]">
                {isEdit ? "Edit instalment plan" : "Set up instalment plan"}
              </p>
              <p className="text-[11px] text-[#9ba3a0]">{dealName}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="size-8 rounded-full flex items-center justify-center hover:bg-[#f7f9f8]"
            aria-label="Close"
          >
            <X className="size-4 text-[#697571]" />
          </button>
        </div>

        <div className="flex flex-1 min-h-0 flex-col md:flex-row">
          {/* ── Left: the inputs ─────────────────────────────────────────── */}
          <div className="w-full md:w-[340px] shrink-0 border-b md:border-b-0 md:border-r border-[#e6e8e7] p-5 overflow-y-auto flex flex-col gap-4">
            {template && !isEdit && (
              <div className="flex items-start gap-2 rounded-xl bg-[#faf8ff] border border-[#e0c6ff] px-3 py-2">
                <Sparkles className="size-3.5 text-[#7c3aed] mt-[2px] shrink-0" />
                <div className="min-w-0">
                  <p className="text-[11px] font-medium text-[#5b21b6]">
                    Prefilled from “{template.name}”
                  </p>
                  <p className="text-[10px] text-[#7c5aa8] leading-[1.45]">
                    {template.installmentCount} instalments · reminder{" "}
                    {template.reminderDaysBefore} day
                    {template.reminderDaysBefore === 1 ? "" : "s"} before. Change
                    anything you like — it only affects this deal.
                  </p>
                </div>
              </div>
            )}

            <div className="flex flex-col gap-1.5">
              <label className="text-[11px] font-medium text-[#374741]">
                {isEdit ? "Amount still to collect" : "Amount to finance"}
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[12px] text-[#697571]">
                  ₹
                </span>
                <Input
                  type="number"
                  value={principal}
                  onChange={(e) => {
                    setTouched(true);
                    setPrincipal(e.target.value);
                  }}
                  className="pl-7 tabular-nums"
                  placeholder="0"
                />
              </div>
              <p className="text-[10px] text-[#9ba3a0]">
                Deal pending: {money(pendingAmount)}
              </p>
              {isEdit && paidInstallments.length > 0 && (
                <p className="text-[10px] text-[#156548] leading-[1.45]">
                  {paidInstallments.length} instalment
                  {paidInstallments.length === 1 ? "" : "s"} already paid (
                  {money(frozenTotal)}) — those stay exactly as they are. Only
                  the {openInstallments.length} unpaid one
                  {openInstallments.length === 1 ? "" : "s"} below get replaced.
                </p>
              )}
              {Math.abs(gap) > 0.005 && (
                <p className="text-[10px] text-[#8a5a2b]">
                  {gap < 0
                    ? `${money(Math.abs(gap))} of the pending balance is not covered by this plan.`
                    : `${money(gap)} more than the deal currently owes.`}
                </p>
              )}
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-[11px] font-medium text-[#374741]">
                Split by
              </label>
              <SegmentToggle
                value={mode}
                onChange={(v) => {
                  setTouched(true);
                  setMode(v as "count" | "amount" | "custom");
                  // Seed the field being switched TO from what is on screen, so
                  // the table does not jump when the operator flips the toggle.
                  if (v === "amount") setPerAmount(derived.perAmount);
                  else if (v === "count") setCount(derived.count);
                  else if (!customRows.length) {
                    // Custom starts from the equal split on screen, row by row.
                    setCustomRows(
                      equalPreview.rows.length
                        ? equalPreview.rows.map((r) => ({ amount: String(r.amount), dueDate: r.dueDate }))
                        : [{ amount: principal, dueDate: startDate }],
                    );
                  }
                }}
                options={[
                  { label: "Number", value: "count" },
                  { label: "Amount", value: "amount" },
                  { label: "Custom", value: "custom" },
                ]}
              />
              {mode === "custom" && (
                <p className="text-[10px] text-[#697571] leading-[1.5]">
                  Set each instalment's amount and date on the right. They must add up to the amount to
                  finance.
                </p>
              )}
            </div>

            {mode !== "custom" && (
            <>
            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-1.5">
                <label className="text-[11px] font-medium text-[#374741]">
                  Instalments
                </label>
                <Input
                  type="number"
                  value={mode === "count" ? count : derived.count}
                  readOnly={mode !== "count"}
                  onChange={(e) => {
                    setTouched(true);
                    setCount(e.target.value);
                  }}
                  className={`tabular-nums ${mode !== "count" ? "bg-[#f7f9f8] text-[#697571]" : ""}`}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-[11px] font-medium text-[#374741]">
                  Each
                </label>
                <Input
                  type="number"
                  value={mode === "amount" ? perAmount : derived.perAmount}
                  readOnly={mode !== "amount"}
                  onChange={(e) => {
                    setTouched(true);
                    setPerAmount(e.target.value);
                  }}
                  className={`tabular-nums ${mode !== "amount" ? "bg-[#f7f9f8] text-[#697571]" : ""}`}
                />
              </div>
            </div>

            <NormalSelector
              title="Frequency"
              value={frequency}
              onChange={(v: string) => {
                setTouched(true);
                setFrequency(v as InstallmentFrequency);
              }}
              options={FREQUENCY_OPTIONS.map((o) => ({ value: o.value, name: o.name }))}
            />

            <div className="flex flex-col gap-1.5">
              <label className="text-[11px] font-medium text-[#374741]">
                First instalment due
              </label>
              <SingleDatePicker
                date={startDate}
                onDateChange={(d) => {
                  setTouched(true);
                  setStartDate(d);
                }}
              />
            </div>
            </>
            )}

            <div className="flex flex-col gap-1.5">
              <label className="text-[11px] font-medium text-[#374741]">
                Remind this many days before
              </label>
              <Input
                type="number"
                min={0}
                max={30}
                value={remindDaysBefore}
                onChange={(e) => {
                  setTouched(true);
                  setRemindDaysBefore(e.target.value);
                }}
                className="tabular-nums"
              />
              <p className="text-[10px] text-[#9ba3a0]">
                Plus a reminder on the day, and once a day while it stays unpaid.
              </p>
            </div>
          </div>

          {/* ── Right: the schedule ──────────────────────────────────────── */}
          <div className="flex-1 min-w-0 flex flex-col">
            <div className="px-5 py-3 border-b border-[#e6e8e7]">
              <p className="text-[10px] font-semibold uppercase tracking-[0.06em] text-[#697571]">
                Schedule
              </p>
            </div>

            {preview.errors.length > 0 && mode !== "custom" ? (
              <div className="flex-1 flex items-center justify-center p-8">
                <div className="text-center max-w-[280px]">
                  <AlertTriangle className="size-5 text-[#c9cfcc] mx-auto mb-2" />
                  {preview.errors.map((e) => (
                    <p key={e} className="text-[12px] text-[#697571]">
                      {e}
                    </p>
                  ))}
                </div>
              </div>
            ) : (
              <div className="flex-1 overflow-y-auto">
                <table className="w-full text-[12px]">
                  <thead className="sticky top-0 bg-white z-[1]">
                    <tr className="text-[9px] uppercase tracking-[0.05em] text-[#9ba3a0] border-b border-[#eef1f0]">
                      <th className="text-left font-medium px-5 py-2 w-12">#</th>
                      <th className="text-left font-medium px-3 py-2">Due date</th>
                      <th className="text-right font-medium px-5 py-2">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mode === "custom" &&
                      customRows.map((r, i) => (
                        <tr key={`c${i}`} className="border-b border-[#f4f6f5]">
                          <td className="px-5 py-1.5 text-[#9ba3a0] tabular-nums">{i + 1}</td>
                          <td className="px-3 py-1.5">
                            <input
                              type="date"
                              value={r.dueDate}
                              onChange={(e) => setCustomRow(i, { dueDate: e.target.value })}
                              aria-label={`Instalment ${i + 1} due date`}
                              className="h-7 rounded-md border border-[#e6e8e7] px-1.5 text-[12px] text-[#374741]"
                            />
                          </td>
                          <td className="px-5 py-1.5 text-right">
                            <div className="flex items-center justify-end gap-1.5">
                              <input
                                type="number"
                                min={0}
                                step="0.01"
                                value={r.amount}
                                onChange={(e) => setCustomRow(i, { amount: e.target.value })}
                                aria-label={`Instalment ${i + 1} amount`}
                                className="h-7 w-28 rounded-md border border-[#e6e8e7] px-1.5 text-right text-[12px] tabular-nums text-[#1e302a]"
                              />
                              <button
                                type="button"
                                onClick={() => {
                                  setTouched(true);
                                  setCustomRows((rows) => rows.filter((_, j) => j !== i));
                                }}
                                aria-label={`Remove instalment ${i + 1}`}
                                className="size-6 flex items-center justify-center rounded-md text-[#9ba3a0] hover:bg-[#fee2e2] hover:text-[#991b1b]"
                              >
                                <Trash2 className="size-3" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    {mode === "custom" && (
                      <tr>
                        <td colSpan={3} className="px-5 py-2">
                          <button
                            type="button"
                            onClick={addCustomRow}
                            className="flex items-center gap-1 text-[11px] font-medium text-[#156548] hover:underline"
                          >
                            <Plus className="size-3" /> Add instalment
                          </button>
                        </td>
                      </tr>
                    )}
                    {mode !== "custom" && preview.rows.map((r) => {
                      const isPast = r.dueDate < today;
                      return (
                        <tr key={r.seq} className="border-b border-[#f4f6f5]">
                          <td className="px-5 py-1.5 text-[#9ba3a0] tabular-nums">
                            {r.seq}
                          </td>
                          <td className="px-3 py-1.5 text-[#374741]">
                            {longDate(r.dueDate)}
                            {isPast && (
                              <span className="ml-1.5 text-[9px] text-[#da2a46]">
                                already past
                              </span>
                            )}
                          </td>
                          <td className="px-5 py-1.5 text-right tabular-nums text-[#1e302a]">
                            {money(r.amount)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* The proof. Rendered even when it balances — that IS the feature. */}
            {preview.rows.length > 0 && (
              <div
                className={`px-5 py-2.5 border-t text-[11px] tabular-nums ${
                  preview.balances
                    ? "border-[#e6e8e7] bg-[#f7f9f8]"
                    : "border-[#f3c9d0] bg-[#fff5f6]"
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-[#697571]">
                    Σ {preview.rows.length} instalments
                  </span>
                  <span className="text-[#1e302a] font-medium">
                    {money(preview.total)}
                  </span>
                </div>
                <div className="flex items-center justify-between mt-0.5">
                  <span className="text-[#697571]">Amount to finance</span>
                  <span
                    className={`font-medium flex items-center gap-1 ${
                      preview.balances ? "text-[#156548]" : "text-[#da2a46]"
                    }`}
                  >
                    {money(principalNum)}
                    {preview.balances ? (
                      <>
                        <Check className="size-3" /> matches
                      </>
                    ) : (
                      <>off by {money(Math.abs(preview.total - principalNum))}</>
                    )}
                  </span>
                </div>
              </div>
            )}

            {mode === "custom" && preview.errors.length > 0 && (
              <div className="px-5 py-2 border-t border-[#f3c9d0] bg-[#fff5f6]">
                {preview.errors.map((e) => (
                  <p key={e} className="text-[10px] text-[#da2a46] leading-[1.5]">
                    {e}
                  </p>
                ))}
              </div>
            )}

            {preview.warnings.length > 0 && (
              <div className="px-5 py-2 border-t border-[#e6e8e7] bg-[#fffbf5]">
                {preview.warnings.map((w) => (
                  <p key={w} className="text-[10px] text-[#8a5a2b] leading-[1.5]">
                    {w}
                  </p>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 px-5 py-3.5 border-t border-[#e6e8e7] bg-white">
          <p className="text-[11px] text-[#697571] tabular-nums truncate">
            {preview.rows.length > 0 && preview.balances
              ? mode === "custom"
                ? `${preview.rows.length} instalments · custom amounts · from ${longDate(preview.rows[0]!.dueDate)}`
                : `${preview.rows.length} instalments · ${money(preview.rows[0]?.amount ?? 0)} each · from ${longDate(preview.rows[0]!.dueDate)}`
              : "Fill in the plan to see the schedule"}
          </p>
          <div className="flex items-center gap-2 shrink-0">
            <Button variant="cancel" size="sm" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button variant="primary" size="sm" onClick={handleSave} disabled={!canSave}>
              {saving
                ? isEdit
                  ? "Saving…"
                  : "Creating…"
                : isEdit
                  ? "Update remaining instalments"
                  : "Create plan"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
